/* Rows for the marketplace homepage and the "Your card" page: the shapes,
   the helpers and the styles. The row itself is components/MarketRow.jsx.

   A row is built from data that already exists, never a copy of it: a person's
   availability post, a company's job post, or a storefront card from the public
   services search. Editing a card writes back to that post or storefront. */

export const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// A Sift Score only earns the right-hand column when it says something good.
// Most profiles are new and score in the 40s and 50s, and a big "43" next to a
// person reads as a grade against them (owner's call, 2026-10-31).
const SCORE_WORTH_SHOWING = 80;

export const CARD_EDIT = { contractor: "/dashboard/contractor/card", company: "/company/card" };

export const today = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};
const parseDay = (iso) => {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};
const shortDate = (d) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });

export function initials(name) {
  return (name || "?").split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";
}

export function daysText(days) {
  if (!days?.length) return "";
  const set = DAYS.filter((d) => days.includes(d));
  if (set.length === 7) return "Every day";
  if (set.join() === "Mon,Tue,Wed,Thu,Fri") return "Mon to Fri";
  if (set.join() === "Sat,Sun") return "Weekends";
  return set.join(", ");
}

// A credential line carries the date it was checked, or it does not render.
// Same rule as VerifiedLicenseBadge: "on file" with no date is a claim about
// today backed by nothing.
function credentialText(creds) {
  const c = (creds || []).find((x) => x.type && x.last_verified_at);
  if (!c) return "";
  const checked = new Date(c.last_verified_at);
  if (Number.isNaN(checked.getTime())) return "";
  return `${c.type} on file, checked ${shortDate(checked)}`;
}

/* The three sources, flattened into one row shape. `owner` says whose card it
   is, so the homepage can offer its owner an Edit link instead of Message. */
export function fromPersonPosting(p) {
  const from = parseDay(p.available_from);
  const now = today();
  const startsNow = !from || from <= now;
  return {
    id: `p-${p.id}`,
    postingId: p.id,
    kind: "person",
    owner: { userId: p.user_id },
    tag: "Available to work",
    title: p.title,
    name: p.poster_name,
    image: p.poster_image_url,
    round: true,
    place: p.location,
    detail: p.description,
    days: p.schedule_days || [],
    fact: credentialText(p.poster_verified_credentials),
    factIsCredential: true,
    key: startsNow ? "Now" : shortDate(from),
    keyNote: "can start",
    start: startsNow ? now : from,
    cta: "Message",
    href: `/postings/${p.id}`,
    editHref: CARD_EDIT.contractor,
  };
}

export function fromOrgPosting(p) {
  return {
    id: `j-${p.id}`,
    postingId: p.id,
    kind: "job",
    owner: { orgId: p.organization_id },
    tag: "Hiring",
    title: p.title,
    name: p.poster_name,
    image: p.poster_image_url,
    round: false,
    place: p.location,
    detail: p.description,
    days: p.schedule_days || [],
    fact: [p.schedule_hours, p.duration].filter(Boolean).join(", "),
    factIsCredential: false,
    key: p.pay_label || "",
    keyNote: p.pay_label ? "pay" : "",
    start: null,
    cta: "Apply",
    href: `/postings/${p.id}`,
    editHref: CARD_EDIT.company,
  };
}

function serviceKey(c) {
  const n = c.rating?.count || 0;
  if (n > 0 && c.rating?.average != null) {
    return { key: Number(c.rating.average).toFixed(1), keyNote: `${n} review${n === 1 ? "" : "s"}` };
  }
  if (c.sift_score >= SCORE_WORTH_SHOWING) return { key: String(c.sift_score), keyNote: "Sift Score" };
  return { key: "", keyNote: "" };
}

export function fromServiceCard(c) {
  const isOrg = c.type === "org";
  return {
    id: `s-${c.type}-${c.slug}`,
    kind: "service",
    owner: isOrg ? { orgSlug: c.slug } : { userSlug: c.slug },
    tag: isOrg ? "Company services" : "Independent pro",
    title: c.name,
    name: "",
    image: c.image_url,
    round: !isOrg,
    place: c.location,
    detail: c.tagline,
    days: [],
    fact: credentialText(c.verified_credentials),
    factIsCredential: true,
    // Reviews when there are any, else a Sift Score worth showing, else
    // nothing: most storefronts have no reviews yet and no stars are shown.
    ...serviceKey(c),
    start: null,
    cta: "Request",
    href: c.url,
    editHref: isOrg ? CARD_EDIT.company : CARD_EDIT.contractor,
  };
}

// Is this row the signed-in viewer's own card? `me` is /api/accounts/me/.
export function isOwnRow(r, me) {
  if (!me || !r.owner) return false;
  const o = r.owner;
  return (o.userId != null && o.userId === me.id)
    || (o.userSlug && o.userSlug === me.public_slug)
    || (o.orgId != null && o.orgId === me.organization?.id)
    || (o.orgSlug && o.orgSlug === me.organization?.slug);
}

// The list a row sits in. Sized by its own width, not the window, so a row in
// a dashboard column folds the same way it does on a phone.
export const MARKET_ROW_CSS = `
.mr-list{list-style:none;margin:0;padding:0;border-top:1px solid #E8E4DC;container-type:inline-size;font-family:var(--font-sans);color:#1F1B16}
.mr-list a{color:inherit;text-decoration:none}
.mr-row{display:flex;gap:20px;padding:24px 0;border-bottom:1px solid #E8E4DC}
.mr-av{flex:0 0 auto;width:64px;height:64px;object-fit:cover;background:#E8E4DC}
.mr-av-ini{display:grid;place-items:center;color:#3D3833;font-size:18px;font-weight:500}
.mr-body{flex:1 1 auto;min-width:0;display:flex;flex-direction:column;gap:4px}
.mr-tag{margin:0;font-size:13px;line-height:18px;font-weight:500;color:#5E554A}
.mr-tag-person{color:#15604A}
.mr-title{font-size:18px;line-height:24px;font-weight:600}
a.mr-title:hover{color:#15604A}
.mr-meta{margin:0;font-size:14px;line-height:20px}
.mr-detail{margin:0;font-size:14px;line-height:20px;color:#5E554A;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.mr-facts{display:flex;flex-wrap:wrap;align-items:center;gap:8px 20px;margin-top:6px;font-size:13px;line-height:18px;color:#5E554A}
.mr-days,.mr-fact{display:inline-flex;align-items:center;gap:8px}
.mr-fact{gap:6px}
.mr-check{color:#15604A}
.mr-dots{display:inline-flex;gap:3px}
.mr-dots span{width:10px;height:10px;border-radius:2px;background:#EFE9DF}
.mr-dots span.on{background:#15604A}
.mr-dots span.need{background:#FFFFFF;box-shadow:inset 0 0 0 1.5px #15604A}
.mr-side{flex:0 0 168px;display:flex;flex-direction:column;align-items:flex-end;gap:4px;text-align:right}
.mr-key{font-size:20px;line-height:26px;font-weight:600;font-variant-numeric:tabular-nums}
.mr-keynote{font-size:13px;line-height:18px;color:#7B7266}
.mr-btn{display:inline-flex;align-items:center;justify-content:center;height:36px;margin-top:8px;padding:0 16px;border-radius:8px;border:1px solid #CFC8BC;background:#FFFFFF;font-size:14px;font-weight:500;white-space:nowrap;transition:border-color .15s cubic-bezier(.22,1,.36,1)}
a.mr-btn:hover{border-color:#15604A}
.mr-sk-b{display:block;background:#F1EEE8;border-radius:6px}
.mr-btn-own{border-color:#15604A;color:#0F4A39!important;background:#EAF1ED}
@container (max-width:640px){
  .mr-row{flex-wrap:wrap;gap:12px 16px}
  .mr-av{width:48px;height:48px}
  .mr-av-ini{font-size:15px}
  .mr-body{flex:1 1 0}
  .mr-side{flex:1 1 100%;flex-direction:row;align-items:baseline;justify-content:flex-start;gap:8px;text-align:left;padding-left:64px}
  .mr-btn{margin-top:0;margin-left:auto}
}
`;
